import api from './client'

export type LoanDirection = 'LENT' | 'BORROWED'
export type InterestBasis = 'NONE' | 'MONTHLY' | 'YEARLY'

export interface EarmarkWarning {
  goals: { id: string; name: string }[]
  shortfall: number
}

export interface PersonalLoan {
  accountId: string
  name: string
  direction: LoanDirection
  contact: { id: string; name: string }
  /** What is still owed, positive either way. */
  outstanding: number
  interestBasis: InterestBasis
  interestRate: number | null
  dueDate: string | null
  closed: boolean
  principalRepaid: number
  interestPaid: number
  writtenOff: number
  notes: string | null
  /** Recorded as already owed before the household used the app. Optional: an older backend omits it. */
  alreadyOwed?: boolean
}

export interface PersonalLoanPayment {
  id: string
  kind: 'PAYMENT' | 'WRITE_OFF'
  paidOn: string
  /** On a WRITE_OFF, the amount written off. */
  principal: number
  interest: number
  note: string | null
  undoable: boolean
}

export interface PersonalLoanDetail {
  loan: PersonalLoan
  /** Most recently recorded first. */
  payments: PersonalLoanPayment[]
  /** The account the principal moved through at creation; null for an attached account. */
  openingAccountName: string | null
}

export interface PersonalLoanWriteResult {
  loan: PersonalLoan
  earmarkWarning?: EarmarkWarning | null
}

export interface CreatePersonalLoanPayload {
  direction: LoanDirection
  contactId: string
  principal: number
  date: string
  /** Omitted when alreadyOwed: no money moves through an account. */
  financialAccountId?: string
  alreadyOwed?: boolean
  interestBasis: InterestBasis
  interestRate?: number
  dueDate?: string
  name?: string
  notes?: string
}

export interface UpdatePersonalLoanPayload {
  contactId: string
  interestBasis: InterestBasis
  interestRate?: number
  dueDate?: string
  /** Absent = unchanged, empty string = clear. */
  notes?: string
}

export interface RecordPaymentPayload {
  paidOn: string
  financialAccountId: string
  principal: number
  interest: number
}

export function listPersonalLoans(contactId?: string): Promise<PersonalLoan[]> {
  return api
    .get('/personal-loans', { params: contactId ? { contactId } : {} })
    .then((r) => r.data.loans)
}

export function getPersonalLoan(accountId: string): Promise<PersonalLoanDetail> {
  return api.get(`/personal-loans/${accountId}`).then((r) => r.data)
}

export function createPersonalLoan(
  payload: CreatePersonalLoanPayload
): Promise<PersonalLoanWriteResult> {
  return api.post('/personal-loans', payload).then((r) => r.data)
}

export function updatePersonalLoan(
  accountId: string,
  payload: UpdatePersonalLoanPayload
): Promise<PersonalLoan> {
  return api.patch(`/personal-loans/${accountId}`, payload).then((r) => r.data)
}

export function recordPersonalLoanPayment(
  accountId: string,
  payload: RecordPaymentPayload
): Promise<PersonalLoanWriteResult> {
  return api.post(`/personal-loans/${accountId}/payments`, payload).then((r) => r.data)
}

export function writeOffPersonalLoan(
  accountId: string,
  writtenOffOn: string
): Promise<PersonalLoanWriteResult> {
  return api.post(`/personal-loans/${accountId}/write-off`, { writtenOffOn }).then((r) => r.data)
}

/** Only for a loan with no payments. */
export function deletePersonalLoan(accountId: string): Promise<void> {
  return api.delete(`/personal-loans/${accountId}`)
}

export function undoPersonalLoanPayment(accountId: string, paymentId: string): Promise<void> {
  return api.delete(`/personal-loans/${accountId}/payments/${paymentId}`)
}
